import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Pin, Copy, Check, AlertCircle } from "lucide-react";
import { Pagination, SortableHeader, MultiSelectStatusFilter, ColumnVisibility, compareValues } from "../components/DataTable";
import useLocalStorageState from "../hooks/useLocalStorageState";
import ConfirmDialog from "../components/ConfirmDialog";

const ICONS = ["TrendingUp", "BarChart3", "Camera", "Image", "FileText", "PenTool", "Sparkles"];
const STATUS_OPTIONS = ["approved", "pending", "rejected"];
const COLUMNS = [
  { key: "order", label: "Order" },
  { key: "title", label: "Title", required: true },
  { key: "slug", label: "Slug" },
  { key: "status", label: "Status" },
  { key: "pinned", label: "Pinned" },
  { key: "active", label: "Active" },
  { key: "created_at", label: "Created" },
  { key: "updated_by_label", label: "Modified by" },
  { key: "updated_at", label: "Modified on" },
];
const statusBadge = {
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  rejected: "bg-rose-100 text-rose-800 border-rose-200",
};

const emptyForm = {
  title: "", slug: "", short_description: "", description: "",
  icon: "Sparkles", benefits: "", price_label: "", order: 0, active: true, pinned: false,
};

export default function AdminServices() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [services, setServices] = useState([]);
  const [statusSel, setStatusSel] = useState(new Set());
  const [mineOnly, setMineOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [deleting, setDeleting] = useState(null);

  const [sortKey, setSortKey] = useState("order");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [hiddenCols, setHiddenCols] = useLocalStorageState("admin_services_hidden_cols_v1", new Set(["updated_by_label", "updated_at"]));
  const toggleCol = (k) => setHiddenCols((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const showCol = (k) => !hiddenCols.has(k);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (mineOnly) params.set("mine", "true");
      const r = await api.get(`/admin/services?${params}`);
      setServices(r.data);
    } finally { setLoading(false); }
  }, [mineOnly]);
  useEffect(() => { load(); }, [load]);

  const toggleStatus = (s) => {
    setPage(1);
    setStatusSel((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  };
  const onSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const filtered = useMemo(() => {
    if (statusSel.size === 0) return services;
    return services.filter((s) => statusSel.has(s.status));
  }, [services, statusSel]);
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => compareValues(a[sortKey], b[sortKey], sortDir));
    return arr;
  }, [filtered, sortKey, sortDir]);
  const total = sorted.length;
  const paged = useMemo(() => sorted.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize), [sorted, page, pageSize]);
  useEffect(() => { setPage(1); }, [pageSize, mineOnly, statusSel.size]);

  const openNew = () => { setEditing("new"); setForm({ ...emptyForm, order: services.length + 1 }); };
  const openEdit = (s) => {
    setEditing(s.id);
    setForm({
      title: s.title, slug: s.slug, short_description: s.short_description, description: s.description,
      icon: s.icon, benefits: (s.benefits || []).join("\n"), price_label: s.price_label || "",
      order: s.order, active: s.active, pinned: !!s.pinned,
    });
  };
  const duplicateService = (s) => {
    const existingSlugs = new Set(services.map((x) => x.slug));
    let candidate = `${s.slug}-copy`;
    let n = 2;
    while (existingSlugs.has(candidate)) candidate = `${s.slug}-copy-${n++}`;
    setEditing("new");
    setForm({
      title: `${s.title} (Copy)`, slug: candidate, short_description: s.short_description,
      description: s.description, icon: s.icon, benefits: (s.benefits || []).join("\n"),
      price_label: s.price_label || "", order: services.length + 1, active: s.active, pinned: false,
    });
    toast.info("Review the copy and click Save to create it");
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        benefits: form.benefits.split("\n").map((b) => b.trim()).filter(Boolean),
        order: Number(form.order) || 0,
        price_label: form.price_label || null,
      };
      if (editing === "new") { await api.post("/services", payload); toast.success("Created"); }
      else { await api.put(`/services/${editing}`, payload); toast.success("Updated"); }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = (svc) => setDeleting(svc);
  const confirmRemove = async () => {
    if (!deleting) return;
    try { await api.delete(`/services/${deleting.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
    finally { setDeleting(null); }
  };
  const approve = async (s) => {
    try { await api.post(`/services/${s.id}/approve`); toast.success("Approved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const submitReject = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/services/${rejecting.id}/reject`, { reason: rejectReason });
      toast.success("Rejected"); setRejecting(null); setRejectReason(""); load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-services-page" className="p-6 lg:p-10 w-full">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-sm uppercase tracking-[0.2em] text-[#525252]">Manage</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">Services</h1>
        </div>
        <button onClick={openNew} data-testid="add-service-button"
                className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white px-5 py-2.5 rounded-sm font-medium inline-flex items-center gap-2">
          <Plus size={16}/> Add Service
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="text-xs uppercase tracking-[0.15em] text-[#525252]">Status:</div>
        <MultiSelectStatusFilter options={STATUS_OPTIONS} selected={statusSel} onToggle={toggleStatus} testIdPrefix="svc-filter"/>
        {!isAdmin && (
          <label className="flex items-center gap-2 text-sm ml-2 cursor-pointer">
            <input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} data-testid="svc-mine-only"/>
            My drafts only
          </label>
        )}
        {statusSel.size > 0 && (
          <button data-testid="svc-clear-filter" onClick={() => setStatusSel(new Set())} className="text-xs text-[#525252] hover:text-[var(--brand-primary)] underline">Clear filter</button>
        )}
        <div className="ml-auto"><ColumnVisibility columns={COLUMNS} hidden={hiddenCols} onToggle={toggleCol} testId="svc-cols"/></div>
      </div>

      {loading ? <div className="text-[#525252]">Loading...</div>
       : sorted.length === 0 ? (
        <div className="bg-white border border-black/10 p-16 text-center text-[#525252]" data-testid="services-empty">No services match the current filter.</div>
       ) : (
        <>
          <div className="bg-white border border-black/10 overflow-x-auto">
            <table className="w-full text-sm" data-testid="services-table">
              <thead>
                <tr className="border-b border-black/10 bg-[#FAFAFA]">
                  {showCol("order") && <th className="px-4 py-3 text-left"><SortableHeader label="Order" sortKey="order" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("title") && <th className="px-4 py-3 text-left"><SortableHeader label="Title" sortKey="title" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("slug") && <th className="px-4 py-3 text-left"><SortableHeader label="Slug" sortKey="slug" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("status") && <th className="px-4 py-3 text-left"><SortableHeader label="Status" sortKey="status" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("pinned") && <th className="px-4 py-3 text-left">Pinned</th>}
                  {showCol("active") && <th className="px-4 py-3 text-left">Active</th>}
                  {showCol("created_at") && <th className="px-4 py-3 text-left"><SortableHeader label="Created" sortKey="created_at" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("updated_by_label") && <th className="px-4 py-3 text-left"><SortableHeader label="Modified by" sortKey="updated_by_label" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("updated_at") && <th className="px-4 py-3 text-left"><SortableHeader label="Modified on" sortKey="updated_at" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((s) => (
                  <tr key={s.id} data-testid={`service-row-${s.slug}`} className="border-b border-black/5 hover:bg-[#FAFAFA]">
                    {showCol("order") && <td className="px-4 py-3 font-mono text-xs text-[#525252]">{String(s.order).padStart(2, "0")}</td>}
                    {showCol("title") && (
                      <td className="px-4 py-3">
                        <div className="font-medium text-[#0A0A0A] truncate max-w-[260px]" title={s.title}>{s.title}</div>
                        <div className="text-xs text-[#525252] truncate max-w-[260px]" title={s.short_description}>{s.short_description}</div>
                        {s.status === "rejected" && s.rejection_reason && (
                          <div className="mt-1 text-[11px] text-rose-700 inline-flex items-center gap-1"><AlertCircle size={11}/> {s.rejection_reason}</div>
                        )}
                      </td>
                    )}
                    {showCol("slug") && <td className="px-4 py-3 text-[#525252] font-mono text-xs">{s.slug}</td>}
                    {showCol("status") && <td className="px-4 py-3"><span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm border ${statusBadge[s.status] || ""}`}>{s.status}</span></td>}
                    {showCol("pinned") && <td className="px-4 py-3">{s.pinned && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[var(--brand-primary)] text-white px-1.5 py-0.5 rounded-sm" data-testid={`pinned-badge-${s.slug}`}><Pin size={10}/> Pinned</span>}</td>}
                    {showCol("active") && <td className="px-4 py-3 text-[#525252] text-xs">{s.active ? "Yes" : "No"}</td>}
                    {showCol("created_at") && <td className="px-4 py-3 text-[#525252] text-xs whitespace-nowrap">{new Date(s.created_at).toLocaleDateString()}</td>}
                    {showCol("updated_by_label") && <td className="px-4 py-3 text-[#525252] text-xs truncate max-w-[200px]" title={s.updated_by_label}>{s.updated_by_label || "—"}</td>}
                    {showCol("updated_at") && <td className="px-4 py-3 text-[#525252] text-xs whitespace-nowrap">{s.updated_at ? new Date(s.updated_at).toLocaleString() : "—"}</td>}
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        {isAdmin && s.status !== "approved" && (
                          <button onClick={() => approve(s)} data-testid={`approve-service-${s.slug}`} title="Approve" className="p-2 hover:bg-emerald-50 rounded-sm text-emerald-700"><Check size={16}/></button>
                        )}
                        {isAdmin && s.status === "pending" && (
                          <button onClick={() => setRejecting(s)} data-testid={`reject-service-${s.slug}`} title="Reject" className="p-2 hover:bg-rose-50 rounded-sm text-rose-700"><AlertCircle size={16}/></button>
                        )}
                        <button onClick={() => duplicateService(s)} data-testid={`duplicate-service-${s.slug}`} title="Duplicate" className="p-2 hover:bg-black/5 rounded-sm"><Copy size={16}/></button>
                        <button onClick={() => openEdit(s)} data-testid={`edit-service-${s.slug}`} title="Edit" className="p-2 hover:bg-black/5 rounded-sm"><Pencil size={16}/></button>
                        {isAdmin && <button onClick={() => remove(s)} data-testid={`delete-service-${s.slug}`} title="Delete" className="p-2 hover:bg-[var(--brand-primary)]/5 rounded-sm text-[var(--brand-primary)]"><Trash2 size={16}/></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={setPageSize}/>
        </>
       )}

      {editing && (
        <div className="fixed inset-0 z-50 flex" data-testid="service-editor-modal">
          <div className="flex-1 bg-black/40" onClick={() => setEditing(null)}/>
          <form onSubmit={save} className="w-full max-w-lg bg-white h-full overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-bold">{editing === "new" ? "New Service" : "Edit Service"}</h2>
              <button type="button" onClick={() => setEditing(null)} className="p-2 hover:bg-black/5"><X size={18}/></button>
            </div>
            <div className="space-y-4">
              <FInput label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testId="service-title" required/>
              <FInput label="Slug" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testId="service-slug" required/>
              <FInput label="Short description" value={form.short_description} onChange={(v) => setForm({ ...form, short_description: v })} testId="service-short-desc" required/>
              <FArea label="Full description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testId="service-desc" required/>
              <div>
                <label className="block text-sm font-medium mb-2">Icon</label>
                <select data-testid="service-icon" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className={inputCls}>
                  {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <FArea label="Benefits (one per line)" value={form.benefits} onChange={(v) => setForm({ ...form, benefits: v })} testId="service-benefits"/>
              <FInput label="Price label (optional)" value={form.price_label} onChange={(v) => setForm({ ...form, price_label: v })} testId="service-price"/>
              <FInput label="Order" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: v })} testId="service-order"/>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="service-active"/>
                Active (visible on public site)
              </label>
              <label className="flex items-start gap-3 cursor-pointer p-3 border border-black/10 rounded-sm hover:bg-[#FAFAFA] transition-colors">
                <input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} data-testid="service-pinned" className="mt-1"/>
                <div>
                  <div className="text-sm font-medium flex items-center gap-1.5"><Pin size={14} className="text-[var(--brand-primary)]"/> Pin to top</div>
                  <div className="text-xs text-[#525252] mt-0.5">Manually pin this service before the auto-detected hot service on the public Services page.</div>
                </div>
              </label>
            </div>
            <button type="submit" disabled={saving} data-testid="save-service-button" className="mt-8 w-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white px-6 py-3 rounded-sm font-medium disabled:opacity-60">
              {saving ? "Saving..." : "Save"}
            </button>
          </form>
        </div>
      )}

      {rejecting && (
        <div className="fixed inset-0 z-50 flex" data-testid="reject-service-modal">
          <div className="flex-1 bg-black/40" onClick={() => { setRejecting(null); setRejectReason(""); }}/>
          <form onSubmit={submitReject} className="w-full max-w-md bg-white h-full overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-bold">Reject Service</h2>
              <button type="button" onClick={() => { setRejecting(null); setRejectReason(""); }} className="p-2 hover:bg-black/5"><X size={18}/></button>
            </div>
            <p className="text-sm text-[#525252] mb-4"><strong>{rejecting.title}</strong> will be marked as rejected. Add a short reason the editor will see:</p>
            <textarea data-testid="reject-reason" rows={4} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="e.g., Description too short, missing pricing details..." className={inputCls}/>
            <button type="submit" data-testid="submit-reject" className="mt-6 w-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white px-6 py-3 rounded-sm font-medium">Reject</button>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => { if (!o) setDeleting(null); }}
        title="Delete this service?"
        description={deleting ? `“${deleting.title}” will be permanently removed. This action can't be undone.` : ""}
        confirmText="Delete"
        destructive
        onConfirm={confirmRemove}
        testId="delete-service-dialog"
      />
    </div>
  );
}

const inputCls = "w-full bg-white border border-black/10 rounded-sm px-3 py-2 focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)] outline-none";

function FInput({ label, value, onChange, testId, type = "text", required }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-2">{label}</label>
      <input data-testid={`input-${testId}`} type={type} required={required} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls}/>
    </div>
  );
}
function FArea({ label, value, onChange, testId, required }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-2">{label}</label>
      <textarea data-testid={`input-${testId}`} required={required} value={value ?? ""} rows={3} onChange={(e) => onChange(e.target.value)} className={inputCls}/>
    </div>
  );
}
